
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useAttendeeRemoval = (eventId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (userId: string) => {
      // First, check if this is an invite ID (UUID format)
      const isInviteId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId);
      
      if (isInviteId) {
        // Remove from event_invites
        const { error } = await supabase
          .from('event_invites')
          .delete()
          .eq('invite_id', userId);
          
        if (error) throw error;
      } else {
        // Remove from event_attendee
        const { error } = await supabase
          .from('event_attendee')
          .delete()
          .eq('event_id', eventId)
          .eq('user_id', userId);
          
        if (error) throw error;
      }
      
      // Return the userId for use in the component
      return userId;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Guest removed successfully",
      });
      
      // Invalidate all relevant queries to force a refresh
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['event-guests-and-host', eventId] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to remove guest",
        variant: "destructive",
      });
      console.error('Error removing guest:', error);
    },
  });

  return {
    removeGuest: mutation.mutateAsync,
    isRemoving: mutation.isPending
  };
};
